<template>
  <v-dialog v-model="modal" :max-width="$vuetify.display.mdAndDown ? '95%' : '60%'" persistent>
    <v-card>
      <v-toolbar color="primary" dark>
        <v-toolbar-title>
          Add Product For
          {{ convertToTitleCase(accountName?.accountNameFriendly || accountName?.name) }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <template v-if="isLoading">
          <div class="height-loader">
            <progress-loader></progress-loader>
          </div>
        </template>

        <template v-else>
          <v-data-table
            :items-per-page="50"
            :headers="headers"
            :items="newProds"
            item-value="name"
            height="600px"
            fixed-header
            style="max-width: 100%; width: 100%; overflow-x: auto"
            class="elevation-1 sticky-header striped-rows"
            dense
          >
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons">
                <v-btn small color="secondary" @click="goToAddProduct(item)">
                  <v-icon small>mdi-plus</v-icon>
                  Add
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAccountStore } from '@/stores/accountStore'
import {
  fetchAccountsProduct,
  fetchProducts,
  getFilteredProductsForAccount,
} from '@/api/accountsAdmin'
import ProgressLoader from '@/components/ProgressLoader.vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const accountStore = useAccountStore()
const products = ref([])
const accountProducts = ref([])
const isLoading = ref(true)

const newProds = ref()

const loadProductsToAdd = async () => {
  try {
    isLoading.value = true
    const data = await getFilteredProductsForAccount(props.accountName.juid)
    console.log(data)
    newProds.value = data // Filter active products
  } catch (error) {
    console.error('Failed to load products for account:', error)
  } finally {
    isLoading.value = false
  }
}

const props = defineProps({
  accountName: String,
  show: Boolean,
})

const emit = defineEmits(['close'])

const modal = ref(props.show)
const selectedAccount = computed(() => accountStore.selectedAccount)

const headers = [
  { title: 'Product Friendly Name', value: 'productFriendlyName', sortable: true },
  { title: 'Product Name', value: 'productName', sortable: true },
  { title: 'Path', value: 'path', sortable: true },
  { title: 'Actions', value: 'actions', sortable: false, align: 'end', width: '150px' },
]

const loadData = async () => {
  try {
    isLoading.value = true

    // Clear previous data
    products.value = []
    accountProducts.value = []

    // Make API calls
    const [allProductsResponse, accountProdsResponse] = await Promise.all([
      fetchProducts(),
      fetchAccountsProduct(props.accountName.juid),
    ])

    // Process all products - they use "product" field
    products.value = allProductsResponse.map((product) => ({
      product: product.product,
      is_active: product.is_active,
      productFriendlyName: product?.productFriendlyName,
    }))

    // Process account products - they use "productName" field
    accountProducts.value = accountProdsResponse.map((prod) => prod.productName)
  } catch (error) {
    console.error('Failed to load data:', error)
  } finally {
    isLoading.value = false
  }
}

const filteredProducts = computed(() => {
  if (!products.value.length || !accountProducts.value.length) {
    return products.value
  }

  return products.value.filter((product) => !accountProducts.value.includes(product?.product))
})

const goToAddProduct = (configProduct) => {
  closeModal()
  router.push(
    `/account/products/${props.accountName.juid}/add-product?productName=${configProduct?.path}`
  )
}

function convertToTitleCase(input: string): string {
  if (!input) return ''
  return input
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const closeModal = () => {
  modal.value = false
  emit('close')
}

watch(
  () => props.show,
  (val) => {
    modal.value = val
    if (val) {
      loadProductsToAdd()
    }
  }
)

watch(
  () => props.accountName,
  () => {
    if (props.accountName) {
      loadProductsToAdd()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.height-loader {
  height: 50vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

h1 {
  margin-bottom: 20px;
}

.custom-button {
  width: 25%;
  min-width: 150px;
}

.new-account-cta-row {
  padding-top: 15px;
  margin: 0;
}

:deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

/* Add this to your style section */
:deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

:deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

:deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
:deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
:deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
:deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}
:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
